/**
 * UIController - Handles all DOM manipulation for the player
 * Updates UI elements based on player state changes
 */
export class UIController {
  constructor() {
    // Cache DOM elements
    this.player = null;
    this.playButton = null;
    this.nextButton = null;
    this.prevButton = null;
    this.skipForwardButton = null;
    this.skipBackwardButton = null;
    this.episodeTitle = null;
    this.currentTimeDisplay = null;
    this.durationDisplay = null;
    this.progressBar = null;
    this.speedToggle = null;
    this.mobileSpeedDisplay = null;
    this.closeButton = null;
  }

  /**
   * Initialize UI controller and cache DOM elements
   */
  init() {
    this.player = document.getElementById('podcast-player');
    this.playButton = document.getElementById('player-play-btn');
    this.nextButton = document.getElementById('player-next-btn');
    this.prevButton = document.getElementById('player-prev-btn');
    this.skipForwardButton = document.getElementById('player-skip-forward');
    this.skipBackwardButton = document.getElementById('player-skip-backward');
    this.episodeTitle = document.getElementById('player-episode-title');
    this.currentTimeDisplay = document.getElementById('player-current-time');
    this.durationDisplay = document.getElementById('player-duration');
    this.progressBar = document.getElementById('player-progress');
    this.speedToggle = document.getElementById('player-speed-toggle');
    this.mobileSpeedDisplay = document.getElementById('mobile-speed-display');
    this.closeButton = document.getElementById('player-close-btn');

    // Initialize progress bar
    if (this.progressBar) {
      this.progressBar.value = 0;
    }
  }

  /**
   * Show the player (slide up from bottom)
   */
  showPlayer() {
    if (this.player) {
      this.player.classList.remove('translate-y-full');
      this.player.classList.add('translate-y-0');
    }
  }

  /**
   * Hide the player (slide down)
   */
  hidePlayer() {
    if (this.player) {
      this.player.classList.remove('translate-y-0');
      this.player.classList.add('translate-y-full');
    }
  }

  /**
   * Update episode information display
   * @param {Object} episode - Episode object with title and metadata
   */
  updateEpisodeInfo(episode) {
    if (this.episodeTitle && episode) {
      this.episodeTitle.textContent = episode.title || 'Unknown Episode';
    }
  }

  /**
   * Update play/pause button state
   * @param {boolean} isPlaying - Whether audio is playing
   */
  updatePlayButton(isPlaying) {
    if (!this.playButton) return;

    // Update desktop play button
    const playIcon = this.playButton.querySelector('.play-icon');
    const pauseIcon = this.playButton.querySelector('.pause-icon');

    if (playIcon && pauseIcon) {
      if (isPlaying) {
        playIcon.classList.add('hidden');
        pauseIcon.classList.remove('hidden');
      } else {
        playIcon.classList.remove('hidden');
        pauseIcon.classList.add('hidden');
      }
    }

    // Update mobile play button
    const mobilePlayIcon = document.querySelector('.mobile-play-icon');
    const mobilePauseIcon = document.querySelector('.mobile-pause-icon');

    if (mobilePlayIcon && mobilePauseIcon) {
      if (isPlaying) {
        mobilePlayIcon.classList.add('hidden');
        mobilePauseIcon.classList.remove('hidden');
      } else {
        mobilePlayIcon.classList.remove('hidden');
        mobilePauseIcon.classList.add('hidden');
      }
    }
  }

  /**
   * Update progress bar and time displays
   * @param {number} currentTime - Current time in seconds
   * @param {number} duration - Total duration in seconds
   */
  updateProgress(currentTime, duration) {
    // Update progress bar
    if (this.progressBar && duration > 0) {
      const percentage = (currentTime / duration) * 100;
      this.progressBar.value = percentage;

      // Update CSS variable for filled progress effect
      this.progressBar.style.setProperty('--progress-percent', `${percentage}%`);

      // Also update mobile progress bar if it exists
      const mobileProgressBar = document.querySelector('input[type="range"][oninput*="player-progress"]');
      if (mobileProgressBar) {
        mobileProgressBar.value = percentage;
        mobileProgressBar.style.setProperty('--progress-percent', `${percentage}%`);
      }
    }

    // Update time displays
    if (this.currentTimeDisplay) {
      this.currentTimeDisplay.textContent = this.formatTime(currentTime);
    }

    if (this.durationDisplay && duration > 0) {
      this.durationDisplay.textContent = this.formatTime(duration);
    }
  }

  /**
   * Update playback speed button display
   * @param {number} rate - Current playback rate
   */
  updateSpeedButton(rate) {
    const speedText = rate === 1 ? '1×' : `${rate}×`;

    if (this.speedToggle) {
      this.speedToggle.textContent = speedText;
    }

    if (this.mobileSpeedDisplay) {
      this.mobileSpeedDisplay.textContent = speedText;
    }
  }

  /**
   * Update next/previous button states based on playlist position
   * @param {boolean} hasNext - Whether there is a next episode
   * @param {boolean} hasPrevious - Whether there is a previous episode
   */
  updateNavigationButtons(hasNext, hasPrevious) {
    if (this.nextButton) {
      this.nextButton.disabled = !hasNext;
      this.nextButton.classList.toggle('opacity-50', !hasNext);
      this.nextButton.classList.toggle('cursor-not-allowed', !hasNext);
    }

    if (this.prevButton) {
      this.prevButton.disabled = !hasPrevious;
      this.prevButton.classList.toggle('opacity-50', !hasPrevious);
      this.prevButton.classList.toggle('cursor-not-allowed', !hasPrevious);
    }
  }

  /**
   * Show loading state
   */
  showLoading() {
    if (this.playButton) {
      this.playButton.disabled = true;
      this.playButton.classList.add('opacity-50');
    }
  }

  /**
   * Hide loading state
   */
  hideLoading() {
    if (this.playButton) {
      this.playButton.disabled = false;
      this.playButton.classList.remove('opacity-50');
    }
  }

  /**
   * Show error message
   * @param {string} message - Error message to display
   */
  showError(message) {
    // Could implement a toast notification or error display
    console.error('Player error:', message);
  }

  /**
   * Format time in seconds to MM:SS or HH:MM:SS
   * @param {number} seconds - Time in seconds
   * @returns {string} Formatted time string
   */
  formatTime(seconds) {
    if (!seconds || isNaN(seconds) || seconds < 0) {
      return '0:00';
    }

    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    } else {
      return `${minutes}:${secs.toString().padStart(2, '0')}`;
    }
  }

  /**
   * Get progress bar element for external event binding
   * @returns {HTMLElement|null}
   */
  getProgressBar() {
    return this.progressBar;
  }

  /**
   * Get play button element for external event binding
   * @returns {HTMLElement|null}
   */
  getPlayButton() {
    return this.playButton;
  }

  /**
   * Get speed toggle button for external event binding
   * @returns {HTMLElement|null}
   */
  getSpeedToggle() {
    return this.speedToggle;
  }
}
